import { useFeatureFlag as useFeatureFlagV2 } from '@harnessio/ff-react-client-sdk';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import {
  CohortDefinitionStatus,
  DerivedEventTypeLegacy,
  FilterTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { useCanAddFilters } from './useCanAddFilters';
import { useCanSaveCohortItem } from './useCanSaveCohortItem';
import { useCohortBuilderEvents } from './useCohortBuilderEvents';
import { useCohortBuilderItemIsInUse } from './useCohortBuilderItemIsInUse';
import { useCohortEditable } from './useCohortEditable';
import { useDerivedEventScroll } from './useDrivedEventScroll';
import { useTemporalConstraints } from './useTemporalConstraints';
import { useUniquenessFilters } from './useUniquenessFilters';
import {
  clearModal,
  setActiveCohortBuilderItemName,
  setActiveCohortBuilderItemDerivedEventType,
  setDerivedEvent,
  setActiveCohortBuilderItemFilterInputFieldValue,
  setActiveCohortBuilderItemAnyOrder,
} from '../cohortBuilderSlice';
import { NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE } from '../constants/constants';
import {
  selectActiveCohortBuilderItemId,
  selectActiveCohortBuilderItemName,
  selectActiveCohortBuilderItemDerivedEventType,
  selectActiveCohortBuilderItemInputFieldConfigMap,
  selectActiveCohortBuilderItemAnyOrder,
} from '../selectors/cohortBuilderEditModalSelectors';
import { selectModal } from '../selectors/cohortBuilderWorkflowSelectors';
import {
  selectCohortDefinition,
  selectDerivedEvents,
  selectSimpleEvents,
  selectSubjectVariables,
} from '../selectors/cohortDefinitionSelectors';
import { validateName } from '../utils/checkNameUniqueness';
import { getFullCohortDslForDerivedEvent } from '../utils/dsl-generators/getFullCohortDslForDerivedEvent';

export const useDerivedEventEdit = () => {
  const dispatch = useAppDispatch();
  const { data: cohortData } = useCohortBuilderData();
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortDefinitionStatus.PROCESSING, CohortDefinitionStatus.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const { isEditable } = useCohortEditable();
  const viewMode = !isEditable || isProcessing;

  const currentCohortDefinition = useAppSelector(selectCohortDefinition);

  const simpleEvents = useAppSelector(selectSimpleEvents);
  const hasAtLeastTwoSimpleEvents = simpleEvents && simpleEvents.length >= 2;

  /** Cohort Builder Workflow - Edit Modal State */
  const derivedEventId = useAppSelector(selectActiveCohortBuilderItemId) ?? uuidv4(); // if no id, then we are creating a new Derived event

  // Summary State
  const modal = useAppSelector(selectModal);
  const name = useAppSelector(selectActiveCohortBuilderItemName) ?? '';
  const type = useAppSelector(selectActiveCohortBuilderItemDerivedEventType);
  const anyOrderChecked = useAppSelector(selectActiveCohortBuilderItemAnyOrder);
  const inputFieldConfigMap =
    useAppSelector(selectActiveCohortBuilderItemInputFieldConfigMap) ?? {};
  const derivedEventIsInUse = useCohortBuilderItemIsInUse(derivedEventId);
  const {
    eventFilters,
    onAddCohortBuilderEventClick,
    onEditCohortBuilderEventInputFieldValue,
    onDeleteCohortBuilderEventClick,
  } = useCohortBuilderEvents();
  const { temporalConstraints, onAddTemporalConstraintClick, onDeleteTemporalConstraintClick } =
    useTemporalConstraints();
  const { uniquenessFilters, onAddUniquenessFilterClick, onDeleteUniquenessFilterClick } =
    useUniquenessFilters();

  const { isSaveAllowed } = useCanSaveCohortItem<DerivedEventTypeLegacy>(name, type, [
    ...eventFilters,
    ...temporalConstraints,
    ...uniquenessFilters,
  ]);

  const { enableAddFilterButtons, addFilterButtonTitleText, typeInputFieldTitleText } =
    useCanAddFilters<DerivedEventTypeLegacy>(name, type, derivedEventIsInUse, [
      DerivedEventTypeLegacy.INSTANCE,
    ]);
  /** user to check for duplicate names */
  const subjectVariables = useAppSelector(selectSubjectVariables);
  const derivedEvents = useAppSelector(selectDerivedEvents);

  const [nameInError, setNameInError] = useState<boolean>(false);
  const [nameErrorMsg, setNameErrorMsg] = useState<string>('');

  /** auto scrolling */
  const { headerRef, dialogContentRef, eventRef, temportalConstraintRef, uniquenessFilterRef } =
    useDerivedEventScroll(eventFilters, temporalConstraints, uniquenessFilters);

  const onNameChange = (id: string, value: string) => {
    dispatch(
      setActiveCohortBuilderItemName({
        id: derivedEventId,
        name: value,
      })
    );

    setNameErrorMsg('');
    setNameInError(false);
  };

  const onNameBlur = (id: string, value: string, nameError: boolean, trimValue: string) => {
    const { errorMsg, hasError } = validateName(
      derivedEventId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  };

  // Now, check on modal/card open (on mount) using useEffect:
  useEffect(() => {
    const { errorMsg, hasError } = validateName(
      derivedEventId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  }, [name, simpleEvents, derivedEvents, subjectVariables, derivedEventId]);

  const onTypeChange = (id: string, value: string) => {
    !derivedEventIsInUse &&
      value &&
      dispatch(
        setActiveCohortBuilderItemDerivedEventType({
          id: derivedEventId,
          type: value as DerivedEventTypeLegacy,
        })
      );
  };

  const onChangeAnyOrderCheckbox = (_: string, value: boolean) => {
    dispatch(setActiveCohortBuilderItemAnyOrder(value));
  };

  const onFilterInputValueChange = (
    filterId: string,
    inputFieldId: string,
    value: string,
    filterType: FilterTypeLegacy
  ) => {
    dispatch(
      setActiveCohortBuilderItemFilterInputFieldValue({
        filterType,
        filterId,
        inputFieldId,
        value,
      })
    );
  };

  /** Cohort Builder Edit Modal Action Handlers */
  const onSaveDerivedEvent = (emitResearchAssistantUpdate: (nextCohortDsl: string) => void) => {
    const _nextDerivedEvent = {
      id: derivedEventId,
      name: name.trim(),
      type: type,
      anyOrder: anyOrderChecked,
      eventFilters,
      temporalConstraints,
      uniquenessFilters,
    };

    dispatch(setDerivedEvent(_nextDerivedEvent));

    /** handle research assistant */
    const nextCohortDsl = getFullCohortDslForDerivedEvent(
      currentCohortDefinition,
      _nextDerivedEvent
    );
    emitResearchAssistantUpdate(nextCohortDsl);
  };

  const onCancelDerivedEvent = () => {
    dispatch(clearModal());
  };

  return {
    modal,
    name,
    type,
    eventFilters,
    temporalConstraints,
    uniquenessFilters,
    inputFieldConfigMap,
    isSaveAllowed,
    enableAddFilterButtons,
    anyOrderChecked,
    derivedEventIsInUse,
    addFilterButtonTitleText,
    typeInputFieldTitleText,
    hasAtLeastTwoSimpleEvents,
    onNameChange,
    onNameBlur,
    nameInError,
    nameErrorMsg,
    nameValidCharacters: NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE,
    onTypeChange,
    onChangeAnyOrderCheckbox,
    onAddTemporalConstraintClick,
    onDeleteTemporalConstraintClick,
    onFilterInputValueChange,
    onAddCohortBuilderEventClick,
    onEditCohortBuilderEventInputFieldValue,
    onDeleteCohortBuilderEventClick,
    onAddUniquenessFilterClick,
    onDeleteUniquenessFilterClick,

    /** Cohort Builder Edit Modal Actions */
    onCancelDerivedEvent,
    onSaveDerivedEvent,

    /** auto scrolling */
    headerRef,
    dialogContentRef,
    eventRef,
    temportalConstraintRef,
    uniquenessFilterRef,
    isEditable,
    viewMode,
  };
};
